import { useMutation } from '@apollo/client';
import { DELETE_COMMUNITY_PROMPT, GET_COMMUNITY_PROMPTS } from '@communities/prompt-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useState, useCallback } from 'react';

interface DeleteCommunityPromptInput {
  promptVersion: string;
}

interface DeleteCommunityPromptResponse {
  deleteCommunityPrompt: {
    success: boolean;
    message: string;
  };
}

export function useDeleteCommunityPrompt() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deletePromptMutation, { loading }] = useMutation<DeleteCommunityPromptResponse>(DELETE_COMMUNITY_PROMPT);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deletePrompt = async (communityId: string, input: DeleteCommunityPromptInput): Promise<boolean> => {
    try {
      track('DeleteCommunityPromptInitiated', {
        communityId,
        promptVersion: input.promptVersion,
      });

      await deletePromptMutation({
        variables: {
          communityId,
          input,
        },
        refetchQueries: communityId ? [{ query: GET_COMMUNITY_PROMPTS, variables: { communityId } }] : [],
        awaitRefetchQueries: true,
      });

      track('DeleteCommunityPromptSucceeded', {
        communityId,
        promptVersion: input.promptVersion,
      });

      return true;
    } catch (error) {
      track('DeleteCommunityPromptFailed', {
        communityId,
        promptVersion: input.promptVersion,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deletePrompt,
    loading,
    errors,
    resetErrors,
  };
}

export default useDeleteCommunityPrompt;
